import type { QueryClient } from "@tanstack/vue-query";
import type { RouteRecordNormalized, RouteRecordRaw, Router } from "vue-router";

import { isNativePlatform } from "../capacitor/platform";
import { loadInBackground } from "./chunkReload";
import { onIdle } from "./idle";

type ChunkLoader = () => Promise<unknown>;

export interface WarmupTarget {
  name: string;
  load: ChunkLoader;
}

// navigator.connection exists in Chrome only.
interface NetworkInformation {
  saveData?: boolean;
  effectiveType?: string;
}

export interface WarmupOptions {
  // A route this session cannot open, so its chunk is not worth fetching.
  cannotOpen?: (record: RouteRecordNormalized) => boolean;
}

const LAST = Number.MAX_SAFE_INTEGER;

/**
 * Fetches the other screens' chunks one at a time once a signed-in screen has
 * rendered. Nothing starts during a navigation or while a query is fetching, so
 * the page on show loads first. Call once, before the first navigation.
 */
export function startRouteWarmup(
  router: Router,
  routes: readonly RouteRecordRaw[],
  queryClient: QueryClient,
  options: WarmupOptions = {},
): void {
  if (isNativePlatform() || browserWantsLessData()) return;

  // Null until a signed-in screen has rendered: the login page warms nothing.
  let toDownload: WarmupTarget[] | null = null;
  let navigationInFlight = false;
  let waitingForIdle = false;

  const pageIsLoading = () => navigationInFlight || queryClient.isFetching() > 0;

  function downloadNextWhenQuiet(): void {
    if (waitingForIdle || !toDownload?.length || pageIsLoading()) return;
    waitingForIdle = true;
    onIdle(() => {
      waitingForIdle = false;
      // A page renders after afterEach, so its requests may have started since.
      if (pageIsLoading()) return;
      const next = toDownload?.shift();
      if (!next) return;
      loadInBackground(next.load).then(downloadNextWhenQuiet, () => {
        // A deploy replaced this tab's files, so every later chunk fails too.
        toDownload = [];
      });
    });
  }

  router.beforeEach(() => {
    navigationInFlight = true;
  });
  router.afterEach((to) => {
    navigationInFlight = false;
    if (toDownload === null && (to.meta.requiresUserLogin || to.meta.requiresAdminLogin)) {
      toDownload = warmupOrder(router, routes, options.cannotOpen);
    }
    downloadNextWhenQuiet();
  });
  router.onError(() => {
    // A failed navigation never reaches afterEach.
    navigationInFlight = false;
    downloadNextWhenQuiet();
  });
  // Picks up again once the page's last request has answered.
  queryClient.getQueryCache().subscribe(downloadNextWhenQuiet);
}

/** Chunks not yet loaded, by `meta.warm`, then signed-in screens first, then declaration order. */
export function warmupOrder(
  router: Router,
  routes: readonly RouteRecordRaw[],
  cannotOpen?: (record: RouteRecordNormalized) => boolean,
): WarmupTarget[] {
  // getRoutes() is ordered by path rank, not by the table, so the table's order is read separately.
  const declared = declaredPosition(routes);
  const ranked = router.getRoutes().flatMap((record) => {
    if (cannotOpen?.(record)) return [];
    const name = record.name ? String(record.name) : record.path;
    return unloadedChunks(record).map((load) => ({
      sortKey: [warmPriority(record), isSignedInScreen(record) ? 0 : 1, declared.get(name) ?? LAST],
      target: { name, load },
    }));
  });

  ranked.sort((a, b) => {
    for (let i = 0; i < a.sortKey.length; i++) {
      if (a.sortKey[i] !== b.sortKey[i]) return a.sortKey[i] - b.sortKey[i];
    }
    return 0;
  });

  return ranked.map((entry) => entry.target);
}

function warmPriority(record: RouteRecordNormalized): number {
  return typeof record.meta.warm === "number" ? record.meta.warm : LAST;
}

function isSignedInScreen(record: RouteRecordNormalized): boolean {
  return Boolean(record.meta.requiresAdminLogin || record.meta.requiresUserLogin);
}

function unloadedChunks(record: RouteRecordNormalized): ChunkLoader[] {
  const loaders: ChunkLoader[] = [];
  for (const name in record.components ?? {}) {
    const component = record.components?.[name];
    // A function is a chunk not yet loaded. The `displayName` test is vue-router's
    // own, and keeps a functional component out.
    if (typeof component === "function" && !("displayName" in component)) {
      loaders.push(component as ChunkLoader);
    }
  }
  return loaders;
}

function declaredPosition(routes: readonly RouteRecordRaw[]): Map<string, number> {
  const position = new Map<string, number>();
  const walk = (list: readonly RouteRecordRaw[]) => {
    for (const route of list) {
      if (route.name && !position.has(String(route.name))) position.set(String(route.name), position.size);
      if (route.children) walk(route.children);
    }
  };
  walk(routes);
  return position;
}

// Data Saver, or a 2G connection.
function browserWantsLessData(): boolean {
  const connection = (navigator as Navigator & { connection?: NetworkInformation }).connection;
  if (!connection) return false;
  if (connection.saveData) return true;
  return /2g$/.test(connection.effectiveType ?? "");
}
